/** @jsxImportSource @emotion/react */
import { DATADOG_ACTIONS } from '@app/app-constants'
import {
  blobToDataUrl,
  preparePageDuplicateMapAndExportImageBlob,
} from '@app/components/Pages/Map/MapTools/ExportMap/mapExporter'
import { toast } from '@app/components/ui/Toast/toast'
import apiClient from '@app/config/axios.config'
import { useFetchLegends } from '@app/hooks/useFetchLegends'
import { usePreferences } from '@app/hooks/usePreferences'
import { userAtom } from '@app/hooks/useUserProfile'
import { projectedRiskChartStateAtom } from '@app/risk-migration/atoms/projectedRiskChart.atoms'
import { selectedIdentifyingInformationAtom } from '@app/risk-migration/atoms/riskIdentifyingInformation.atoms'
import { drawAreasAtom, regionMasksAtom } from '@app/risk-migration/atoms/riskFilters.atoms'
import { visibleAssetsAtom } from '@app/risk-migration/atoms/riskLayers.atoms'
import {
  cascadingAssetNDaysAtom,
  riskMapInstanceAtom,
} from '@app/risk-migration/atoms/riskMap.atoms'
import { useRiskLayersState } from '@app/risk-migration/hooks/useRiskLayersState'
import { clientNameAtom } from '@app/state/clientsAtom'
import { projectedRiskChartDataAtom } from '@app/state/projectedRiskChartDataAtom'
import { logDataDogAction } from '@app/utils/logger'
import { pollForReport } from '@app/utils/pollForReport'
import { MenuItem, useTheme } from '@mui/material'
import * as turf from '@turf/turf'
import { Polygon, Position } from 'geojson'
import { useAtomValue } from 'jotai'
import { MakeProjectedRiskChartSvg } from '../../Charts/ProjectedRiskChart/ProjectedRiskChart'
import { renderChartSvgString, getConsequencePalette } from '../../Charts/utils/utils'
import { generateVulnerabilityPalette } from '../../MapView/RiskMapView/RiskMapView.utilities'
import { RegionOption } from '../../RegionFilter'
import { getLayerId } from '../../RiskSideDrawerContent/data_fetchers/layerDetailsFetcher'
import { exportButton } from './ExportRiskSummaryXlxs.styles'

const PROJECTED_RISK_CHART_EXPORT_WIDTH = 500

export const RiskSummaryReportButton = () => {
  const theme = useTheme()
  const visibleAssets = useAtomValue(visibleAssetsAtom)
  const drawAreas = useAtomValue(drawAreasAtom)
  const { data: legendsData = {} } = useFetchLegends()
  const { layers } = useRiskLayersState()
  const projectedRiskChartState = useAtomValue(projectedRiskChartStateAtom)
  const { term_preference } = usePreferences()
  const regionMasks = useAtomValue(regionMasksAtom)
  const map = useAtomValue(riskMapInstanceAtom)
  const cascadingAssetNDays = useAtomValue(cascadingAssetNDaysAtom)
  const vulnerabilityPalette = generateVulnerabilityPalette(legendsData, 'vulnerability')
  const clientName = useAtomValue(clientNameAtom)
  const { data: user } = useAtomValue(userAtom)
  const projectedRiskChartData = useAtomValue(projectedRiskChartDataAtom)
  const consequencePalette = getConsequencePalette(legendsData['vulnerability'])
  const selectedIdentifyingInformation = useAtomValue(selectedIdentifyingInformationAtom)
  const handleClick = async () => {
    if (!map) return
    toast({
      description:
        'Generating data for report. This may take a while, we will notify you when the report is ready.',
      type: 'info',
    })

    const regions = regionMasks ? regionMasks.map((mask: RegionOption) => mask.region) : []
    const draw_area_coordinates: Position[][] | null =
      drawAreas.length > 0 ? (drawAreas[0].geometry as Polygon).coordinates : null

    let mapImageDataUrl = ''
    const mapWidth = 595
    const mapHeight = 559
    await preparePageDuplicateMapAndExportImageBlob(
      map,
      mapWidth,
      mapHeight,
      async (imageBlob: Blob) => {
        mapImageDataUrl = await blobToDataUrl(imageBlob)
      },
      vulnerabilityPalette,
      null,
    )

    const mapHeightInPixels = map.getCanvas().height
    const mapWidthInPixels = map.getCanvas().width
    const mapWidthInMetres = turf.distance(
      map.unproject([0, mapHeightInPixels / 2.0]).toArray(),
      map.unproject([mapWidthInPixels, mapHeightInPixels / 2.0]).toArray(),
      { units: 'meters' },
    )

    try {
      const mapImageUploadResponse = await apiClient.post(`/report/mapimage`, {
        dataUrl: mapImageDataUrl,
      })

      logDataDogAction(DATADOG_ACTIONS.EXPORT_REPORT, {
        type: 'risk-summary',
        format: 'pdf',
      })

      const mapImageDownloadUrl = mapImageUploadResponse.data as string
      const layer_ids = layers.map((l) => getLayerId(l))

      const report_id = `${user?.user_uuid}-${Date.now()}`

      const projectedRiskChartSvgs: {
        [assetId: string]: {
          [hazardId: string]: { riskCurvesSvg?: string; projectedConsequenceSvg?: string }
        }
      } = {}

      if (consequencePalette) {
        for (const [assetId, byHazard] of Object.entries(projectedRiskChartData)) {
          for (const [hazardId, series] of Object.entries(byHazard)) {
            const riskCurvesSvg = series.riskCurves
              ? renderChartSvgString((element) =>
                  MakeProjectedRiskChartSvg({
                    ...series.riskCurves!,
                    svgElement: element,
                    width: PROJECTED_RISK_CHART_EXPORT_WIDTH,
                    consequencePalette,
                  }),
                )
              : null
            const projectedConsequenceSvg = series.projectedConsequence
              ? renderChartSvgString((element) =>
                  MakeProjectedRiskChartSvg({
                    ...series.projectedConsequence!,
                    svgElement: element,
                    width: PROJECTED_RISK_CHART_EXPORT_WIDTH,
                    consequencePalette,
                  }),
                )
              : null

            if (riskCurvesSvg || projectedConsequenceSvg) {
              if (!projectedRiskChartSvgs[assetId]) projectedRiskChartSvgs[assetId] = {}
              projectedRiskChartSvgs[assetId][hazardId] = {
                ...(riskCurvesSvg ? { riskCurvesSvg } : {}),
                ...(projectedConsequenceSvg ? { projectedConsequenceSvg } : {}),
              }
            }
          }
        }
      }

      const mappedVisibleAssets = visibleAssets
        ? Object.keys(visibleAssets).reduce(
            (acc, assetType) => {
              acc[assetType] = Object.keys(visibleAssets[assetType])
              return acc
            },
            {} as { [key: string]: string[] },
          )
        : visibleAssets

      await apiClient.post(`/report/export/${clientName}/risksummary-pdf/${report_id}`, {
        layer_ids,
        draw_area_coordinates,
        regions,
        mapImage: {
          bearing: map.getBearing(),
          pitch: map.getPitch(),
          mapWidthInMetres,
          url: mapImageDownloadUrl,
        },
        projectedRiskChartState,
        projectedRiskChartSvgs,
        cascadingAssetNDays,
        selectedIdentifyingInformation,
        prefiltered_asset_ids: mappedVisibleAssets,
      })

      const downloadFilename = term_preference.hazard
        ? 'Resilience Explorer® Hazard Summary'
        : `Resilience Explorer® Risk Summary`

      pollForReport(report_id, downloadFilename)
    } catch (error) {
      toast({
        description:
          'There was an error generating the report. Please notify your Urban Intelligence contact or submit feedback.',
        type: 'error',
      })
    }
  }

  return (
    <MenuItem css={exportButton({ theme })} onClick={handleClick}>
      Report (PDF)
    </MenuItem>
  )
}
